<template>
  <UPage class="flex-1 flex flex-col min-h-0" :ui="{ center: 'flex flex-col flex-1 min-h-0' }">
    <UPageHeader
      :title="(productDetails.fldSuchBeg as string) || 'Artikeldetails'"
      :description="`Nummer: ${fldArtNr}`"
    >
      <template #links>
        <UButton
          icon="i-heroicons-arrow-left"
          label="Zurück zu Artikeln"
          variant="ghost"
          @click="$router.push('/microtech-hub/products')"
        />
      </template>
    </UPageHeader>

    <UPageBody class="flex flex-col min-h-0 h-full">
      <p class="mb-3 text-xs text-muted" data-testid="active-tenant-marker">
        Mandant: {{ customerNo }} / {{ tenantName }}
      </p>
      <UAlert
        v-if="loadError"
        class="mb-4"
        color="error"
        icon="i-heroicons-exclamation-triangle"
        role="alert"
        title="Artikeldetails konnten nicht geladen werden"
        :description="loadError"
        variant="subtle"
      />
      <ColumnSelectionModal
        ref="columnModal"
        :all-columns="allPossibleColumns as any"
        :initial-selected="selectedTabColumnsMapped as any"
        :loading="fetchingSchema"
        title="Felder anpassen"
        description="Wählen Sie die Felder aus, die in der Detailansicht angezeigt werden sollen."
        @save="onSaveColumns"
        @reset="resetToDefault"
      />

      <UTabs
        v-model="activeTab"
        :items="tabs"
        class="flex-1 flex flex-col min-h-0 h-full"
        :ui="{ content: 'flex-1 flex flex-col min-h-0 h-full' }"
      >
        <template #details>
          <UCard>
            <template #header>
              <div class="flex items-center justify-between">
                <h3 class="text-base font-semibold">Stammdaten</h3>
                <UButton
                  color="neutral"
                  icon="i-heroicons-cog-6-tooth"
                  label="Felder anpassen"
                  size="xs"
                  variant="ghost"
                  @click="openColumnModal('details')"
                />
              </div>
            </template>
            <div v-if="loading" class="flex justify-center py-10">
              <UIcon class="w-10 h-10 animate-spin text-muted" name="i-heroicons-arrow-path" />
            </div>
            <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-4">
              <div
                v-for="field in selectedColumns"
                :key="field.accessorKey || field.name"
                class="border-b border-default pb-2"
              >
                <p class="text-xs font-medium text-muted uppercase tracking-wider">
                  {{ field.label }}
                </p>
                <p class="mt-1 text-sm text-highlighted">
                  {{ resolveValue(productDetails, field) || '-' }}
                </p>
              </div>
            </div>
          </UCard>
        </template>

        <template #bi>
          <ArticleDashboardComponent :fld-art-nr="fldArtNr" />
        </template>

        <template #dokumente>
          <PortalTable
            ref="dokumenteTable"
            :columns-loading="fetchingSchema"
            :data="relatedData.dokumente"
            :loading="loadingRelated.dokumente"
            :selected-columns="selectedColumnsByTab.dokumente as any"
            :all-columns="availableColumnsByTab.dokumente as any"
            :total="relatedData.dokumente.length"
            :sort-storage-key="DOC_SORT_STORAGE_KEY"
            class="flex-1"
            highlight-row-key="fldGUID"
            :highlight-row-value="($route.query.highlightGUID as string)"
            @select="(row) => downloadDocument(row.original)"
            @update:columns="onSaveColumns as any"
            @reset:columns="resetToDefault"
          >
            <template #header>
              <div class="flex items-center justify-between p-4 border-b border-default">
                <h3 class="text-base font-semibold">Dokumente</h3>
                <UButton
                  color="neutral"
                  icon="i-heroicons-cog-6-tooth"
                  label="Felder anpassen"
                  size="xs"
                  variant="ghost"
                  @click="openColumnModal('dokumente')"
                />
              </div>
            </template>
            <template #body-bottom>
              <div
                v-if="hasNextPageByTab.dokumente"
                v-intersection-observer="() => fetchRelatedConRead('dokumente', true)"
                class="h-1 w-full"
              />
            </template>
          </PortalTable>
        </template>
      </UTabs>
    </UPageBody>
  </UPage>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import PortalTable from '@/components/PortalTable.vue'
import ColumnSelectionModal from '@/components/ColumnSelectionModal.vue'
import ArticleDashboardComponent from '@/components/bi/ArticleDashboardComponent.vue'
import { useProductStore, type Column } from '@/stores/product'
import { fetchTableFields, fetchRelationFields } from '@/utils/graphql-introspection'
import type { FieldInfo } from '@/utils/graphql-introspection'
import { useAuthStore } from '@/stores/auth'
import { useDocumentDownload } from '@/composables/useDocumentDownload'

const props = defineProps<{
  fldArtNr: string
}>()

const authStore = useAuthStore()
const productStore = useProductStore()

const customerNo = computed(() => authStore.userInfo?.activeTenant?.customerNo || '')
const tenantName = computed(() => authStore.userInfo?.activeTenant?.tenantName || '')

const loading = ref(false)
const fetchingSchema = ref(false)
const productDetails = ref<Record<string, unknown>>({})
const loadError = ref('')
const allPossibleColumns = ref<FieldInfo[]>([])
const selectedColumns = ref<FieldInfo[]>([])
const route = useRoute()
const router = useRouter()
const activeTab = ref(route.query.tab as string || 'details')

const loadingRelated = ref<Record<string, boolean>>({
  dokumente: false,
})
const relatedData = ref<Record<string, Record<string, unknown>[]>>({
  dokumente: [],
})
const endCursorByTab = ref<Record<string, string>>({
  dokumente: '',
})
const hasNextPageByTab = ref<Record<string, boolean>>({
  dokumente: false,
})
const limit = ref(100)

const availableColumnsByTab = ref<Record<string, FieldInfo[]>>({
  details: [],
  dokumente: [],
})
const selectedColumnsByTab = ref<Record<string, FieldInfo[]>>({
  details: [],
  dokumente: [],
})

const activeTabKey = computed(() => activeTab.value)
const selectedTabColumnsMapped = computed(() => {
  return selectedColumnsByTab.value[activeTabKey.value] || []
})

watch(
  () => route.query.tab,
  (newTab) => {
    if (newTab && typeof newTab === 'string') {
      activeTab.value = newTab
    } else if (!route.path.endsWith('/bi')) {
      activeTab.value = 'details'
    }
  },
)

watch(
  () => route.path,
  (newPath) => {
    if (newPath.endsWith('/bi')) {
      activeTab.value = 'bi'
    } else if (activeTab.value === 'bi') {
      activeTab.value = (route.query.tab as string) || 'details'
    }
  },
)

watch(activeTab, (val) => {
  const currentTabInQuery = route.query.tab
  const expectedTabInQuery = val === 'details' || val === 'bi' ? undefined : val

  if (val === 'bi') {
    if (!route.path.endsWith('/bi')) {
      router.push({
        path: `/microtech-hub/products/${props.fldArtNr}/bi`,
        query: { ...route.query, tab: undefined },
      })
    }
  } else {
    const targetPath = `/microtech-hub/products/${props.fldArtNr}`
    if (route.path.endsWith('/bi')) {
      router.push({
        path: targetPath,
        query: { ...route.query, tab: expectedTabInQuery },
      })
    } else if (currentTabInQuery !== expectedTabInQuery) {
      router.push({
        query: { ...route.query, tab: expectedTabInQuery },
      })
    }
  }

  if (val === 'dokumente' && relatedData.value.dokumente.length === 0) {
    fetchRelatedConRead('dokumente')
  }
})

if (route.path.endsWith('/bi')) {
  activeTab.value = 'bi'
} else {
  activeTab.value = (route.query.tab as string) || 'details'
}

const columnModal = ref<InstanceType<typeof ColumnSelectionModal> | null>(null)

const tabs = [
  { label: 'Details', slot: 'details', value: 'details' },
  { label: 'Auswertungen', slot: 'bi', value: 'bi' },
  { label: 'Dokumente', slot: 'dokumente', value: 'dokumente' }
]

const STORAGE_KEY = computed(() => `cc_prod_det_cols_${customerNo.value}_${tenantName.value}`)
const DOC_STORAGE_KEY = computed(() => `cc_prod_det_doc_cols_${customerNo.value}_${tenantName.value}`)
const DOC_SORT_STORAGE_KEY = computed(() => `cc_prod_det_doc_sort_${customerNo.value}_${tenantName.value}`)

async function fetchMetadata() {
  if (!customerNo.value || !tenantName.value || fetchingSchema.value) return
  fetchingSchema.value = true
  try {
    const fields = await fetchTableFields('tblProducts', customerNo.value, tenantName.value)
    const dynamicColumns: FieldInfo[] = []

    for (const f of fields) {
      if (
        f.name.startsWith('_') ||
        f.name.endsWith('_int') ||
        f.name.startsWith('tbl') ||
        f.name.startsWith('lnk')
      )
        continue

      if (f.name.startsWith('row')) {
        // Relationen auflösen (z.B. rowWgNr)
        if (f.name === 'rowWgNr') {
          try {
            const relFields = await fetchRelationFields(f.type, customerNo.value, tenantName.value)
            const prefix = 'Warengruppe'
            relFields.forEach((rf) => {
              if (rf.isRelation || rf.name.startsWith('_')) return
              dynamicColumns.push({
                ...rf,
                name: rf.name,
                type: rf.type,
                kind: rf.kind,
                isRelation: false,
                accessorKey: `${f.name}.${rf.name}`,
                header: rf.label || `${prefix}: ${rf.name}`,
                label: rf.label || `${prefix}: ${rf.name}`,
                graphql: rf.graphql ? `${f.name} { ${rf.graphql} }` : `${f.name} { ${rf.name} }`,
              })
            })
          } catch (e) {
            console.warn(`Failed to fetch relation fields for ${f.name} (${f.type})`, e)
          }
        }
        continue
      }

      dynamicColumns.push({
        ...f,
        key: f.name,
        accessorKey: f.name,
        label: f.label || f.description || f.name,
        header: f.label || f.description || f.name,
        isRelation: false,
      })
    }

    availableColumnsByTab.value.details = dynamicColumns
    loadSavedColumns()
  } catch (error) {
    console.error('Fehler beim Laden der Metadaten:', error)
  } finally {
    fetchingSchema.value = false
  }
}

async function fetchSchemaForTab(tabKey: string) {
  if (tabKey === 'details' || tabKey === 'bi') return
  if (!customerNo.value || !tenantName.value) return
  fetchingSchema.value = true
  try {
    let typeName = ''
    if (tabKey === 'dokumente') typeName = 'DocumentRowQueryRead'

    const fields = await fetchRelationFields(typeName, customerNo.value, tenantName.value)
    availableColumnsByTab.value[tabKey] = fields
      .filter((f) => !f.name.startsWith('_') && !f.name.endsWith('_int'))
      .map((f) => ({
        ...f,
        key: f.name,
        accessorKey: f.name,
        label: f.label || f.description || f.name,
        header: f.label || f.description || f.name,
        graphql: f.graphql || f.name,
      }))
  } finally {
    fetchingSchema.value = false
  }
}

function loadSavedColumns() {
  const saved = productStore.loadColumns(STORAGE_KEY.value)
  if (saved) {
    const updated = (saved as unknown as FieldInfo[]).map((s) => {
      const found = availableColumnsByTab.value.details.find((c) => c.accessorKey === s.accessorKey)
      return found ? { ...s, label: found.label, header: found.label, graphql: found.graphql } : s
    })
    selectedColumns.value = updated
    selectedColumnsByTab.value.details = updated
  } else {
    setDefaultColumns()
  }

  const savedDocs = productStore.loadColumns(DOC_STORAGE_KEY.value)
  if (savedDocs) {
    selectedColumnsByTab.value.dokumente = savedDocs as unknown as FieldInfo[]
  } else {
    setDefaultDocColumns()
  }
}

function setDefaultColumns() {
  const defaultKeys = ['fldArtNr', 'fldKuBez1', 'fldBez1', 'fldSuchBeg']
  const defaults = availableColumnsByTab.value.details.filter(c => defaultKeys.includes(c.accessorKey || ''))
  selectedColumns.value = defaults
  selectedColumnsByTab.value.details = defaults
  productStore.setColumns(STORAGE_KEY.value, defaults as unknown as Column[], false)
}

function setDefaultDocColumns() {
  const defaults: FieldInfo[] = [
    { name: 'fldNa', label: 'Name', graphql: 'fldNa', accessorKey: 'fldNa', key: 'fldNa', type: 'string', kind: 'SCALAR', isRelation: false },
    { name: 'fldExt', label: 'Erweiterung', graphql: 'fldExt', accessorKey: 'fldExt', key: 'fldExt', type: 'string', kind: 'SCALAR', isRelation: false },
  ]
  selectedColumnsByTab.value.dokumente = defaults
  productStore.setColumns(DOC_STORAGE_KEY.value, defaults as unknown as Column[], false)
}

async function fetchProductDetails() {
  if (!customerNo.value || !tenantName.value || !props.fldArtNr || (selectedColumns.value.length === 0 && allPossibleColumns.value.length === 0)) return
  loading.value = true
  loadError.value = ''
  productDetails.value = {}
  try {
    const columnsToFetch = [...selectedColumns.value]
    if (!columnsToFetch.some(c => c.accessorKey === 'fldSuchBeg')) {
      const found = allPossibleColumns.value.find(c => c.accessorKey === 'fldSuchBeg')
      if (found) {
        columnsToFetch.push(found)
      }
    }

    const selectionSet = buildSelectionSet(columnsToFetch)
    const query = `
      query {
        tblProducts {
          rowRead(kf1ArtNr: { string: "${props.fldArtNr}" }) {
            ${selectionSet}
          }
        }
      }
    `
    const response = await axios.post('/api/microtech/v1/proxy/graphql', { query })
    const details = response.data?.data?.tblProducts?.rowRead
    if (!details) {
      loadError.value = 'Der ERP-Datensatz ist für den aktiven Mandanten nicht verfügbar.'
      return
    }
    productDetails.value = details
  } catch (error) {
    console.error('Fehler beim Laden der Artikeldetails:', error)
    loadError.value = 'Bitte versuchen Sie es erneut oder prüfen Sie die microtech-Verbindung.'
  } finally {
    loading.value = false
  }
}

function buildSelectionSet(columns: FieldInfo[]) {
  const tree: Record<string, unknown> = {}
  columns.forEach((col) => {
    const key = col.graphql || col.accessorKey || col.name || col.key || ''
    if (!key) return
    const parts = key.split('.')
    let current = tree
    parts.forEach((part: string) => {
      if (!current[part]) current[part] = {}
      current = current[part] as Record<string, unknown>
    })
  })

  function stringify(obj: Record<string, unknown>): string {
    return Object.keys(obj)
      .map((k) => {
        const val = obj[k]
        if (val && typeof val === 'object' && Object.keys(val).length > 0) {
          return `${k} { ${stringify(val as Record<string, unknown>)} }`
        }
        return k
      })
      .join('\n')
  }
  return stringify(tree)
}

function resolveValue(obj: Record<string, unknown>, field: FieldInfo) {
  const key = field.accessorKey || field.name || field.key || ''
  if (!key) return undefined
  return key.split('.').reduce((o: unknown, i: string) => (o && typeof o === 'object' ? (o as Record<string, unknown>)[i] : undefined), obj)
}

async function fetchRelatedConRead(tabKey: string, loadMore = false) {
  if (loadMore && !hasNextPageByTab.value[tabKey]) return
  if (loadingRelated.value[tabKey]) return

  loadingRelated.value[tabKey] = true
  const selectionSet = buildSelectionSet(selectedColumnsByTab.value[tabKey])
  const afterParam =
    loadMore && endCursorByTab.value[tabKey] ? `, after: "${endCursorByTab.value[tabKey]}"` : ''

  let rootField = ''
  let filter = ''

  if (tabKey === 'dokumente') {
    rootField = 'tblDocuments'
    filter = `allBetween: { byNr: { kf1Nr: { string: "${props.fldArtNr}" } } }
              fastFilter: { eq: [{ field: fldDBBkzBez }, { value: "Art" }] }`
  }

  const query = `
    query {
      ${rootField} {
        conRead(
          first: ${limit.value}
          ${afterParam}
          ${filter}
        ) {
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              fldID
              ${tabKey === 'dokumente' ? 'fldGUID' : ''}
              ${selectionSet}
            }
          }
        }
      }
    }
  `

  try {
    const response = await axios.post('/api/microtech/v1/proxy/graphql', { query })
    const conRead = response.data?.data?.[rootField]?.conRead
    if (conRead) {
      const newRows = conRead.edges.map((edge: { node: Record<string, unknown> }) => edge.node)
      if (loadMore) {
        relatedData.value[tabKey] = [...relatedData.value[tabKey], ...newRows]
      } else {
        relatedData.value[tabKey] = newRows
      }
      endCursorByTab.value[tabKey] = conRead.pageInfo.endCursor
      hasNextPageByTab.value[tabKey] = conRead.pageInfo.hasNextPage
    }
  } catch (error) {
    console.error(`Error fetching ${tabKey}:`, error)
  } finally {
    loadingRelated.value[tabKey] = false
  }
}

const { downloadDocument } = useDocumentDownload()

async function openColumnModal(tabKey: string = 'details') {
  if (tabKey === 'dokumente' && availableColumnsByTab.value.dokumente.length === 0) {
    await fetchSchemaForTab(tabKey)
  }
  allPossibleColumns.value = availableColumnsByTab.value[tabKey as keyof typeof availableColumnsByTab.value]
  columnModal.value?.open()
}

function onSaveColumns(columns: unknown[]) {
  const tabKey = activeTabKey.value
  if (tabKey === 'details') {
    selectedColumns.value = columns as FieldInfo[]
    selectedColumnsByTab.value.details = columns as FieldInfo[]
    productStore.setColumns(STORAGE_KEY.value, columns as unknown as Column[])
    fetchProductDetails()
  } else if (tabKey === 'dokumente') {
    selectedColumnsByTab.value.dokumente = columns as FieldInfo[]
    productStore.setColumns(DOC_STORAGE_KEY.value, columns as unknown as Column[])
    fetchRelatedConRead(tabKey)
  }
}

function resetToDefault() {
  const tabKey = activeTabKey.value
  if (tabKey === 'details') {
    productStore.removeColumns(STORAGE_KEY.value)
    setDefaultColumns()
    fetchProductDetails()
  } else if (tabKey === 'dokumente') {
    productStore.removeColumns(DOC_STORAGE_KEY.value)
    setDefaultDocColumns()
    fetchRelatedConRead(tabKey)
  }
}

onMounted(async () => {
  if (customerNo.value && tenantName.value) {
    await fetchMetadata()
    fetchProductDetails()
    if (activeTab.value === 'dokumente') {
      fetchRelatedConRead('dokumente')
    }
  }

  watch(() => props.fldArtNr, () => {
    fetchProductDetails()
    if (activeTab.value === 'dokumente') {
      fetchRelatedConRead('dokumente')
    }
  })

  watch(activeTab, (val) => {
    if (val === 'dokumente' && relatedData.value.dokumente.length === 0) {
      fetchRelatedConRead('dokumente')
    }
  })

  watch([customerNo, tenantName], async ([newCust, newTen]) => {
    if (newCust && newTen) {
      await fetchMetadata()
      fetchProductDetails()
      if (activeTab.value === 'dokumente') {
        fetchRelatedConRead('dokumente')
      }
    }
  })
})
</script>
